/**
 * The run-history chart's arithmetic and wording, without React.
 * RidgeWill/org-admin#111.
 *
 * Pure and exported for tests, like fleet-view.ts. fleet-api sends every job in
 * the window as a raw row (its src/fleet/runs.ts says why), so everything the
 * chart shows is derived here: each job's outcome, time slots on the viewer's
 * clock, the counts and median per slot, axis ticks, and the filters.
 */
import type { RunJob } from './fleet-api.js';

export type Outcome = 'success' | 'failure' | 'cancelled' | 'skipped' | 'running';

/**
 * In stacking order, bottom up, which is also the legend's order.
 *
 * Status colours, not series colours: an outcome MEANS good or bad. Shape
 * carries it too on the scatter, because success and failure sit close for a
 * deuteranope on the dark theme (the palette validator put the pair at 6.5).
 *
 * Cancelled and skipped were one outcome, "Cancelled or skipped", until Jon
 * found the grouped label messy (13 Sep). They are different events: a
 * cancelled job was stopped, usually superseded by a newer push; a skipped one
 * was never needed.
 */
export const OUTCOMES: { key: Outcome; label: string }[] = [
  { key: 'success', label: 'Succeeded' },
  { key: 'failure', label: 'Failed' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'skipped', label: 'Skipped' },
  { key: 'running', label: 'Running' },
];

/**
 * GitHub's status and conclusion, reduced to what the chart tells apart.
 *
 * `timed_out` and `startup_failure` are failures: the job was meant to do its
 * work and did not. `cancelled` and `action_required` (held for an approval
 * nobody gave) are jobs that were stopped. `skipped`, `neutral` and `stale`
 * did no work and were not meant to.
 */
export function outcomeOf(job: Pick<RunJob, 'status' | 'conclusion'>): Outcome {
  if (job.status !== 'completed') return 'running';
  switch (job.conclusion) {
    case 'success':
      return 'success';
    case 'failure':
    case 'timed_out':
    case 'startup_failure':
      return 'failure';
    case 'skipped':
    case 'neutral':
    case 'stale':
      return 'skipped';
    default:
      return 'cancelled';
  }
}

/** When a job happened, for placing it in time: when it started, or when it was queued if it never did. */
export const jobTime = (job: Pick<RunJob, 'started_at' | 'created_at'>): number =>
  Date.parse(job.started_at ?? job.created_at);

export interface Range {
  key: string;
  label: string;
  /** The range in a sentence: "in the last {words}". */
  words: string;
  hours: number;
  /** Slot width. Chosen so every range draws 24 to 30 bars. */
  slotMinutes: number;
}

export const RANGES: Range[] = [
  { key: '1h', label: '1h', words: 'hour', hours: 1, slotMinutes: 2 },
  { key: '4h', label: '4h', words: '4 hours', hours: 4, slotMinutes: 10 },
  { key: '12h', label: '12h', words: '12 hours', hours: 12, slotMinutes: 30 },
  { key: '1d', label: '1D', words: 'day', hours: 24, slotMinutes: 60 },
  { key: '2d', label: '2D', words: '2 days', hours: 48, slotMinutes: 120 },
  { key: '7d', label: '7D', words: '7 days', hours: 168, slotMinutes: 360 },
  { key: '30d', label: '30D', words: '30 days', hours: 720, slotMinutes: 1440 },
];

/** An unknown key falls back to a day, the range Blacksmith opens on. */
export function rangeOf(key: string | undefined): Range {
  return RANGES.find((r) => r.key === key) ?? RANGES.find((r) => r.key === '1d')!;
}

export interface Slot {
  start: number;
  end: number;
  counts: Record<Outcome, number>;
  total: number;
  /** Nearest-rank median of finished jobs' run time: a duration a real job took. */
  medianDuration_s: number | null;
  /** The same, for time spent waiting for a runner. Kept apart from run time on purpose (#96). */
  medianQueue_s: number | null;
}

/** Nearest-rank median, matching fleet-api's stats.ts. */
export function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.ceil(sorted.length / 2) - 1]!;
}

/**
 * The window's slots, with each job counted into the one it started in.
 *
 * Boundaries land on the VIEWER's clock: hour slots on the hour, six-hour slots
 * at 00, 06, 12 and 18, day slots at local midnight. fleet-api sends raw rows
 * so this can happen here. `offsetMinutes` is injectable so a test is not at the
 * mercy of the machine's zone, as with `heatmap`.
 *
 * The offset is read once, at the window's start. A 30-day window crossing a
 * daylight-saving change has its later day slots an hour off local midnight,
 * which is a smaller fault than slots 23 or 25 hours long.
 */
export function slotsFor(
  jobs: RunJob[],
  range: Range,
  now: number,
  offsetMinutes: (ms: number) => number = (ms) => -new Date(ms).getTimezoneOffset(),
  /** Where the window starts, when it is not simply `range.hours` before `now`: a clicked bar's slot. */
  from?: number,
): Slot[] {
  const slotMs = range.slotMinutes * 60_000;
  const windowStart = from ?? now - range.hours * 3_600_000;
  const shift = offsetMinutes(windowStart) * 60_000;
  const first = Math.floor((windowStart + shift) / slotMs) * slotMs - shift;

  const slots: Slot[] = [];
  const durations: number[][] = [];
  const queues: number[][] = [];
  for (let start = first; start <= now; start += slotMs) {
    slots.push({
      start,
      end: start + slotMs,
      counts: { success: 0, failure: 0, cancelled: 0, skipped: 0, running: 0 },
      total: 0,
      medianDuration_s: null,
      medianQueue_s: null,
    });
    durations.push([]);
    queues.push([]);
  }

  for (const job of jobs) {
    const t = jobTime(job);
    // `!(t >= first)` rather than `t < first`, so an unparseable time is dropped too.
    if (!(t >= first) || t > now) continue;
    const i = Math.floor((t - first) / slotMs);
    const slot = slots[i];
    if (!slot) continue;
    slot.counts[outcomeOf(job)] += 1;
    slot.total += 1;
    if (job.status === 'completed' && job.duration_s !== null) durations[i]!.push(job.duration_s);
    if (job.queue_s !== null) queues[i]!.push(job.queue_s);
  }

  slots.forEach((slot, i) => {
    slot.medianDuration_s = median(durations[i]!);
    slot.medianQueue_s = median(queues[i]!);
  });
  return slots;
}

/** 0 up to a round top in 1, 2 or 5 steps, at most five ticks. At least 0 and 1. */
export function countTicks(max: number): number[] {
  if (!(max > 0)) return [0, 1];
  const rough = max / 4;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const step = Math.max(1, [1, 2, 5, 10].map((m) => m * magnitude).find((s) => s >= rough) ?? 10 * magnitude);
  const top = Math.ceil(max / step) * step;
  return Array.from({ length: top / step + 1 }, (_, i) => i * step);
}

const DURATION_STEPS = [15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 10800, 21600, 43200, 86400];

/**
 * 0 up to a round top in steps a person reads as durations (30s, 1m, 5m, 1h),
 * at most five ticks. Past the table, whole days: a self-hosted job can run for
 * days, and one outlier must not turn the axis into a ladder of fifty labels.
 */
export function durationTicks(maxSeconds: number): number[] {
  const max = maxSeconds > 0 ? maxSeconds : 60;
  const step = DURATION_STEPS.find((s) => max / s <= 4) ?? Math.ceil(max / 4 / 86400) * 86400;
  const top = Math.ceil(max / step) * step;
  return Array.from({ length: top / step + 1 }, (_, i) => i * step);
}

/** Nearest-rank percentile: always a value that occurred. */
export function percentile(values: number[], p: number): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length, Math.max(1, Math.ceil((p / 100) * sorted.length))) - 1]!;
}

/**
 * The scatter's duration axis, and how many jobs sit above it.
 *
 * Scaled to the slowest job unless that job is an outlier. On 13 Sep three
 * cancelled auth runs of about four hours flattened 4,500 jobs of under two
 * minutes into one line along the bottom of the 30-day scatter. So when the
 * slowest is more than twice the 99th percentile, the axis stops at the 99th,
 * and the jobs above it are drawn on the top edge, counted, with their real
 * durations in the tooltip.
 */
export function scatterAxis(durations: number[]): { ticks: number[]; top: number; above: number } {
  const max = durations.length ? Math.max(...durations) : 0;
  const p99 = percentile(durations, 99) ?? 0;
  const bound = p99 > 0 && max > 2 * p99 ? p99 : max;
  const ticks = durationTicks(bound);
  const top = ticks[ticks.length - 1]!;
  return { ticks, top, above: durations.filter((d) => d > top).length };
}

/** An axis label: "45s", "5m", "1h 30m". Ticks are round, so this stays short where `seconds` would say "6h 00m". */
export function durationLabel(total: number): string {
  if (total === 0) return '0';
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  return [h ? `${h}h` : '', m ? `${m}m` : '', sec ? `${sec}s` : ''].filter(Boolean).join(' ');
}

const FOCUS_SLOT_MINUTES = [1, 2, 5, 10, 30, 60, 120, 360, 1440];

/**
 * A clicked bar's time slot, as a range of its own.
 *
 * Jon, 13 Sep: clicking a day's bars narrows the run history to that day. The
 * slots inside are the smallest step that keeps the narrowed chart to 30 bars
 * or fewer, so a day opens in hours and an hour in two-minute slots.
 */
export function focusRange(start: number, end: number): Range {
  const minutes = Math.max(1, Math.round((end - start) / 60_000));
  const slotMinutes = FOCUS_SLOT_MINUTES.find((m) => minutes / m <= 30) ?? 1440;
  return { key: 'focus', label: 'Selected', words: 'selected time', hours: minutes / 60, slotMinutes };
}

/** Jobs placed inside [start, end), by the same time the chart places them. */
export function inWindow(jobs: RunJob[], start: number, end: number): RunJob[] {
  return jobs.filter((j) => {
    const t = jobTime(j);
    return t >= start && t < end;
  });
}

/** Empty string means "all". */
export interface RunFilter {
  repo: string;
  workflow: string;
  kind: string;
}

export const NO_FILTER: RunFilter = { repo: '', workflow: '', kind: '' };

export function filterJobs(jobs: RunJob[], filter: RunFilter): RunJob[] {
  return jobs.filter(
    (j) =>
      (!filter.repo || j.repo === filter.repo) &&
      (!filter.workflow || j.workflow === filter.workflow) &&
      (!filter.kind || j.kind === filter.kind),
  );
}

/**
 * What each filter can be set to, from the jobs in the window.
 *
 * Workflows are listed for the chosen repo only, because `test` in auth and
 * `test` in keyodo are different workflows with one name, and a list of every
 * repo's workflows would offer combinations that match nothing.
 */
export function filterOptions(jobs: RunJob[], filter: RunFilter): { repos: string[]; workflows: string[] } {
  const repos = [...new Set(jobs.map((j) => j.repo))].sort();
  const workflows = [...new Set(jobs.filter((j) => !filter.repo || j.repo === filter.repo).map((j) => j.workflow))].sort();
  return { repos, workflows };
}

/** The job's page on GitHub. Built from ids fleet-api already has, so nothing extra is stored. */
export function jobUrl(job: Pick<RunJob, 'repo' | 'run_id' | 'id'>): string {
  return `https://github.com/RidgeWill/${encodeURIComponent(job.repo)}/actions/runs/${job.run_id}/job/${job.id}`;
}

/** GitHub's trigger names in words. Unknown ones pass through as GitHub spells them. */
export function triggerLabel(event: string | null): string {
  switch (event) {
    case null:
      return 'Unknown trigger';
    case 'push':
      return 'Push';
    case 'pull_request':
      return 'Pull request';
    case 'schedule':
      return 'Schedule';
    case 'workflow_dispatch':
      return 'Manual';
    case 'workflow_run':
      return 'Workflow run';
    default:
      return event;
  }
}
